import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { loginUser } from "../services/authService";
import { getLoggedInUser } from "../utils/auth";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { getLoginImage } from "../services/imageService";

const schema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup.string().required("Password is required"),
});

const SignInLayer = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // ✅ added
  const [loginImg, setLoginImg] = useState("assets/images/auth/auth-img.png");

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const res = await getLoginImage();
        if (res?.status === "success" && res?.data) {
          let imgUrl = null;
          if (Array.isArray(res.data)) {
            if (res.data.length > 0) {
              imgUrl = res.data[0].image_url;
            }
          } else if (typeof res.data === "object") {
            imgUrl = res.data.image_url;
          }
          if (imgUrl) {
            setLoginImg(imgUrl);
          }
        }
      } catch (error) {
        console.error("Failed to load custom login image", error);
      }
    };
    fetchImage();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const res = await loginUser(data.email, data.password);

      if (res?.status === "success" || res?.status === true) {
        if (res?.token) localStorage.setItem("token", res.token);
        toast.success(res?.message || "Login Successful");

        const loggedUser = getLoggedInUser();
        const userRole = (res?.data?.role || loggedUser?.role || "").toLowerCase();
        setTimeout(() => {
          if (userRole === "operator") {
            navigate("/operator-files");
          } else {
            navigate("/");
          }
        }, 500);
      } else {
        toast.error(res?.message || "Invalid email or password");
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Login failed. Try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auth bg-base d-flex flex-wrap">
      <div className="auth-left d-lg-block d-none">
        <div className="d-flex align-items-center flex-column h-100 justify-content-center">
          <img src={loginImg} alt="Auth" onError={(e) => {
            e.target.onerror = null;
            e.target.src = "assets/images/auth/auth-img.png";
          }} />
        </div>
      </div>

      <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
        <div className="max-w-464-px mx-auto w-100">

          <div>
            <Link to="/" className="mb-40 max-w-290-px">
              <img src="assets/images/logo.png" alt="" />
            </Link>
            <h4 className="mb-12">Sign In to your Account</h4>
            <p className="mb-32 text-secondary-light text-lg">
              Welcome back! please enter your detail
            </p>
          </div>

          {/* FORM */}
          <form onSubmit={handleSubmit(onSubmit)}>

            {/* Email */}
            <div className="icon-field mb-16">
              <span className="icon top-50 translate-middle-y">
                <Icon icon="mage:email" />
              </span>
              <input
                type="email"
                className={`form-control h-56-px bg-neutral-50 radius-12 ${
                  errors.email ? "is-invalid" : ""
                }`}
                placeholder="Email"
                {...register("email")}
              />
              <p className="text-danger small mt-1">
                {errors.email?.message}
              </p>
            </div>

            {/* Password */}
            <div className="position-relative mb-20">
              <div className="icon-field position-relative">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="solar:lock-password-outline" />
                </span>

                <input
                  type={showPassword ? "text" : "password"} // ✅ changed
                  className={`form-control h-56-px bg-neutral-50 radius-12 ${
                    errors.password ? "is-invalid" : ""
                  }`}
                  placeholder="Password"
                  {...register("password")}
                />

                {/* 👁 Show / Hide Icon */}
                <span
                  className="position-absolute top-50 end-0 translate-middle-y me-16 cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>

              <p className="text-danger small mt-1">
                {errors.password?.message}
              </p>
            </div>

            {/* Remember */}
            <div className="d-flex justify-content-between gap-2 mb-2">
              <div className="form-check style-check d-flex align-items-center">
                <input
                  className="form-check-input border border-neutral-300"
                  type="checkbox"
                  id="remember"
                />
                <label className="form-check-label" htmlFor="remember">
                  Remember me
                </label>
              </div>
            </div>

            {/* Button */}
            <button
              type="submit"
              className="btn btn-primary text-sm justify-content-center px-12 py-16 w-100 radius-12 mt-32"
              disabled={loading}
            >
              {loading ? "Please wait..." : "Sign In"}
            </button>

            {/* Privacy Policy Link */}
            <div className="mt-24 text-center">
              <Link
                to="/privacy-policy"
                className="text-secondary-light hover-text-primary text-sm fw-medium d-inline-flex align-items-center gap-2"
              >
                <Icon icon="solar:shield-check-bold-duotone" className="text-lg text-primary-600" />
                Privacy Policy
              </Link>
            </div>

          </form>
        </div>
      </div>
    </section>
  );
};

export default SignInLayer;
